import React, { useState, useEffect } from "react";
import { entitiesApi } from "../lib/api";
import { toast } from "sonner";
import { Buildings, MagnifyingGlass, Plus, Check, X } from "@phosphor-icons/react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

const TYPE_BADGES = {
  company:      "bg-blue-50 text-blue-700 border border-blue-200",
  organisation: "bg-slate-100 text-slate-600 border border-slate-200",
  product:      "bg-violet-50 text-violet-700 border border-violet-200",
  theme:        "bg-amber-50 text-amber-700 border border-amber-200",
  person:       "bg-rose-50 text-rose-700 border border-rose-200",
};

export default function EntitiesPage() {
  const [entities,  setEntities]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [q,         setQ]         = useState("");
  const [showForm,  setShowForm]  = useState(false);
  const [form,      setForm]      = useState({ name: "", entity_type: "company", description: "" });
  const [saving,    setSaving]    = useState(false);

  const load = async (query) => {
    setLoading(true);
    try {
      const { data } = await entitiesApi.list(query || undefined);
      setEntities(data);
    } catch { toast.error("Failed to load entities"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    load(q.trim() || undefined);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const { data } = await entitiesApi.create(form);
      setEntities((ent) => [data, ...ent]);
      setForm({ name: "", entity_type: "company", description: "" });
      setShowForm(false);
      toast.success("Entity added");
    } catch { toast.error("Failed to add entity"); }
    finally { setSaving(false); }
  };

  const byType = entities.reduce((acc, e) => {
    (acc[e.entity_type] = acc[e.entity_type] || []).push(e);
    return acc;
  }, {});

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-1">Entities</p>
          <h1 className="font-display text-[22px] font-bold text-slate-900 tracking-tight">
            Entities <span className="text-slate-400 font-normal">({entities.length})</span>
          </h1>
          <p className="text-[13px] text-slate-500 mt-1">
            Companies, agencies and organisations mentioned across your intelligence sources.
          </p>
        </div>
        <Button onClick={() => setShowForm((s) => !s)} size="sm">
          <Plus size={13} /> Add entity
        </Button>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2 mb-6">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search entities…"
            className="w-full pl-7 pr-3 py-2 text-xs border border-slate-200 rounded bg-white placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
          />
        </div>
        <button type="submit" className="text-xs border border-slate-200 px-3 py-2 rounded hover:bg-slate-50 transition-colors text-slate-600">
          Search
        </button>
        {q && (
          <button type="button" onClick={() => { setQ(""); load(); }} className="text-xs text-slate-500 hover:text-slate-700">
            Clear
          </button>
        )}
      </form>

      {/* Add form */}
      {showForm && (
        <form onSubmit={submit} className="mb-6 border border-slate-200 rounded-md p-5 bg-white space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">Name</label>
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Sandvik, BYD, ARENA"
                className="w-full text-xs border border-slate-200 rounded px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-slate-400"
                required
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">Type</label>
              <select
                value={form.entity_type}
                onChange={(e) => setForm((f) => ({ ...f, entity_type: e.target.value }))}
                className="w-full text-xs border border-slate-200 rounded px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-slate-400"
              >
                <option value="company">Company</option>
                <option value="organisation">Organisation</option>
                <option value="product">Product</option>
                <option value="theme">Theme</option>
                <option value="person">Person</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">Description (optional)</label>
            <input
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Brief description"
              className="w-full text-xs border border-slate-200 rounded px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-slate-400"
            />
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button type="button" onClick={() => setShowForm(false)} className="text-[13px] text-slate-500 hover:text-slate-700">Cancel</button>
            <Button type="submit" disabled={saving} size="sm" className="ml-auto">
              <Check size={12} /> {saving ? "Adding…" : "Add entity"}
            </Button>
          </div>
        </form>
      )}

      {/* Grid by type */}
      {loading ? (
        <div className="text-xs text-slate-400 py-12 text-center">Loading entities…</div>
      ) : entities.length === 0 ? (
        <div className="text-center py-16">
          <Buildings size={28} className="text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No entities tracked yet.</p>
          <p className="text-xs text-slate-400 mt-1">Entities are auto-extracted by Hermes or added manually.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(byType).map(([type, items]) => (
            <div key={type}>
              <h2 className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-3">
                {type.charAt(0).toUpperCase() + type.slice(1)}s ({items.length})
              </h2>
              <div className="grid gap-2.5 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {items.map((ent) => (
                  <div key={ent.id} className="border border-slate-200 bg-white rounded-lg px-3.5 py-3 hover:shadow-sm transition-shadow">
                    <div className="flex items-start gap-2 mb-1.5">
                      <h3 className="text-[14px] font-semibold text-slate-900 flex-1 leading-snug">{ent.name}</h3>
                      <Badge variant={ent.entity_type === "organisation" ? "industry" : ent.entity_type === "product" ? "theory" : ent.entity_type === "theme" ? "anomaly" : ent.entity_type === "person" ? "contrarian" : "insight"} className="shrink-0">
                        {ent.entity_type.slice(0, 3).toUpperCase()}
                      </Badge>
                    </div>
                    {ent.description && (
                      <p className="text-[11px] text-slate-500 leading-relaxed line-clamp-2 mb-1.5">{ent.description}</p>
                    )}
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-[10px] text-slate-400">
                        {ent.mention_count} mention{ent.mention_count !== 1 ? "s" : ""}
                      </span>
                      {(ent.tags || []).slice(0, 2).map((t) => (
                        <span key={t} className="text-[10px] font-mono text-slate-400 bg-slate-50 border border-slate-100 px-1 py-0.5 rounded">{t}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
